<template>
  <VDialog
    v-model="isDialogVisible"
    class="v-dialog-sm dialog-purchase"
    max-width="400px"
    persistent
    no-click-animation
  >
    <VCard class="br16">
      <div class="pop-inner">
        <div class="pop-tit-box">
          <div class="">구매하기</div>
          <div class="close-button" @click="close()">Close</div>
        </div>

        <div class="purchase-content">
          <div class="purchase-content__body">
            <div class="purchase-content__top">
              <div class="purchase-content-box-title">
                <div class="title-1">
                  <span class="bg-title">{{ category }}</span>
                </div>
                <div class="title-2-box">
                  <div class="title-2">{{ name }}</div>
                  <div class="title-3">{{ description }}</div>
                </div>
                <div v-if="props.selectedOption" class="title-4">옵션 : {{ optionName }}</div>
              </div>

              <div class="line-box-01"></div>

              <div class="purchase-content-box">
                <div class="title-5">결제 내역</div>
                <div class="purchase-content-box-item">
                  <div class="purchase-content-box-item-title">상품가(옵션포함)</div>
                  <div class="purchase-content-box-item-price">{{ totalBasePoint }} P</div>
                </div>
                <div class="purchase-content-box-item">
                  <div class="purchase-content-box-item-title">상품할인</div>
                  <div class="purchase-content-box-item-price">- {{ discountedPoint }} P</div>
                </div>
              </div>
            </div>

            <div class="purchase-content-box__wrapper">
              <div class="purchase-content-box-total">
                <div class="purchase-content-box-total-title">총 결제할 포인트</div>
                <div class="purchase-content-box-total-price">{{ totalPoint }} P</div>
              </div>
              <div>
                <div class="purchase-content-box-total">
                  <div class="purchase-content-box-total-title">보유 포인트</div>
                  <div class="purchase-content-box-remain-point">
                    {{ remainPoints?.toLocaleString() }} P
                  </div>
                </div>
                <div class="purchase-content-box-total">
                  <div class="purchase-content-box-total-title">부족한 포인트</div>
                  <div class="purchase-content-box-remain-point">
                    {{ shortageAmount?.toLocaleString() }} P
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="btn-com">
            <button class="btn-nor submit" @click="handlePurchase">구매하기</button>
          </div>
        </div>
      </div>
    </VCard>
  </VDialog>
  <ConfirmComponent
    ref="confirmRef"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="dialogWarning"
  />
  <Snackbar ref="snackbarRef" />
</template>

<script setup>
import { useRouter } from 'vue-router';
import ConfirmComponent from '@/components/common/Confirm.vue';
import { usePaymentsStore } from '@/store/modules/payments';
import { useStore } from 'vuex';
import { createOrder } from '@/api/PaymentApi';
import { DEV_MODE } from '@/config/dev-mode';
import { useSnackbar } from '@/composables/useSnackbar';
import { useDialogManager } from '@/composables/useDialogManager';
import { useRnBridge } from '@/composables/useRnBridge';
import Snackbar from '@/components/common/Snackbar.vue';
import axios from 'axios';

const store = useStore();
const paymentsStore = usePaymentsStore();
const route = useRoute();
const router = useRouter();
const { snackbarRef, triggerSnackbar } = useSnackbar();
const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogNextUrl,
  dialogWarning,
  confirmRef,
  showConfirm,
} = useDialogManager();
const { sendMessageToApp } = useRnBridge();
const props = defineProps({
  ticketData: {
    type: Object,
  },
  optionItems: {
    type: Object,
  },
  selectedOption: {
    type: Object,
  },
  totalPrice: {
    type: Number,
    default: 0,
    required: false,
  },
  totalBasePrice: {
    type: Number,
    default: 0,
    required: false,
  },
  accessToken: {
    type: String,
  },
});

const ticketData = computed(() => props.ticketData);
const isDialogVisible = ref(false);
const emit = defineEmits(['close', 'purchase-success']);

/** 잔여 포인트 체크 */
const isShortage = computed(
  () => Number(totalPoint.value.replaceAll(',', '')) > remainPoints.value,
);
const remainPoints = computed(() => paymentsStore.points || 0);
const shortageAmount = computed(() =>
  isShortage.value ? Number(totalPoint.value.replaceAll(',', '')) - remainPoints.value : 0,
);

const userId = ref(null);
userId.value = store.getters['user/id'];

const close = () => {
  isDialogVisible.value = false;
  emit('close');
};

const totalBasePoint = computed(() => {
  const value = props.totalBasePrice || ticketData.value.product.basePointPrice;
  return value.toLocaleString();
});

const totalPoint = computed(() => {
  const value = props.totalPrice || ticketData.value.product.salePointPrice;
  return value.toLocaleString();
});

const discountedPoint = computed(() => {
  const original = props.totalBasePrice || ticketData.value.product.basePointPrice;
  const current = props.totalPrice || ticketData.value.product.salePointPrice;
  const discount = original - current;
  return discount.toLocaleString();
});
const name = computed(() => ticketData.value.product.name);
const description = computed(() => ticketData.value.product.description);
const category = computed(() => ticketData.value.boardCategory?.categoryCtn);

const optionName = computed(() => {
  if (props.selectedOption?.optionId) {
    const selectedOptionItem = props.optionItems?.find(
      (item) => item.value === props.selectedOption.optionId,
    );
    if (selectedOptionItem) {
      const [mainTitle] = selectedOptionItem.title.split('+');
      return mainTitle.trim();
    }
  }

  const rawTitle = props.optionItems?.[0]?.title ?? '';
  const [mainTitle] = rawTitle.split('+');
  return mainTitle.trim();
});

const confirmLogin = () => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({ action: 'login' });
  } else {
    router.push({ path: '/auth/login' });
  }
};

const confirmCharge = () => {
  // next URL에 optionId를 포함시켜서 충전 후 돌아올 때 복원
  let nextPath = route.fullPath;
  if (props.selectedOption?.optionId) {
    const url = new URL(nextPath, window.location.origin);
    url.searchParams.set('optionId', props.selectedOption.optionId);
    nextPath = url.pathname + url.search;
  }
  router.push({
    path: '/purchase/points',
    query: {
      next: nextPath,
      shortageAmount: shortageAmount.value,
    },
  });
};

const handlePurchase = () => {
  if (!userId.value && !props.accessToken) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
    });
  } else if (Number(totalPoint.value.replaceAll(',', '')) > Number(paymentsStore.points)) {
    showConfirm({
      title: '보유 낭만 포인트가 부족합니다.',
      title2: '지금 바로 충전하시겠습니까?',
      text: '충전하기',
      onConfirm: () => confirmCharge(),
      warning: `${shortageAmount.value.toLocaleString()} P 부족`,
    });
  } else {
    showConfirm({
      title: '선택한 상품으로 포인트 결제를 진행합니다.',
      text: '확인',
      onConfirm: () => orderPlanTicket(),
    });
  }
};

const orderPlanTicket = async () => {
  const params = {
    amount: 0,
    pointAmount: props.totalPrice || ticketData.value.product.salePointPrice,
    originalAmount: 0,
    originalPointAmount: props.totalBasePrice || ticketData.value.product.basePointPrice,
    productId: ticketData.value.product.productId,
    selectedOptions: props.selectedOption ? [props.selectedOption] : [],
    orderName: `${ticketData.value.product.name}`,
  };

  try {
    await createOrder(params);
    paymentsStore.fetchTicketCount();
    paymentsStore.getOwnPoint();

    close(); // Close purchase popup first
    triggerSnackbar('구매가 완료되었습니다.');
    emit('purchase-success');
  } catch (error) {
    DEV_MODE && console.error('분석권구매실패', error);

    const errorMessage =
      error?.response?.data?.detail || error?.message || '구매 중 오류가 발생했습니다.';
    triggerSnackbar(errorMessage);
  }
};

const open = () => {
  isDialogVisible.value = true;
};

onMounted(() => {
  paymentsStore.getOwnPoint();
});
defineExpose({
  open,
  close,
});

watch(
  () => props.accessToken,
  (newValue) => {
    if (newValue) {
      axios.defaults.headers.common['Authorization'] = newValue;
      paymentsStore.getOwnPoint();
    }
  },
);
</script>

<style scoped lang="scss">
.purchase-content {
  padding: 8px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  .purchase-content__body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 60px;
  }

  .purchase-content__top {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .line-box-01 {
      background: var(--Gray-300, #ebebeb);
      height: 1px;
      width: 100%;
      margin: 20px 0;
    }
  }

  .btn-com {
    width: 100%;
  }
}

.purchase-content-box-title {
  gap: 8px;
  display: flex;
  flex-direction: column;

  .bg-title {
    display: inline-flex;
    color: var(--gray-100-white, #fff);
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    border-radius: 4px;
    background: var(--Gray-600, #757575);
    padding: 2px 5px;
    align-items: center;
  }

  .title-2 {
    color: var(--Gray-800, #333);
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  .title-3 {
    color: var(--Gray-600, #757575);
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }

  .title-4 {
    color: var(--orange-300-main, #eb4c10);
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
  }
}

.purchase-content-box {
  padding: 0 24px;
  display: flex;
  flex-direction: column;

  .title-5 {
    color: var(--Gray-800, #333);
    text-align: left;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    margin-bottom: 12px;
  }

  .purchase-content-box-item {
    display: flex;
    padding: 4px 12px;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
  }
}

.purchase-content-box__wrapper {
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 12px;
  background: var(--Orange-100, #fff8f5);
}

.purchase-content-box-total {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .purchase-content-box-total-title {
    color: var(--Gray-800, #333);
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }

  .purchase-content-box-total-price {
    color: var(--orange-300-main, #eb4c10);
    font-size: 22px;
    font-weight: 700;
    line-height: 26px;
  }

  .purchase-content-box-remain-point {
    color: var(--orange-300-main, #eb4c10);
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
  }
}

@media (max-width: 768px) {
  .purchase-content {
    padding: 0px 16px 16px;
    gap: 16px;

    .purchase-content__body {
      gap: 16px;
    }

    .purchase-content__top .line-box-01 {
      margin: 10px 0;
    }
  }

  .purchase-content-box {
    padding: 0 16px;
  }

  .purchase-content-box__wrapper {
    padding: 12px 16px;
  }
}
</style>
